import { useCallback, useEffect, useRef, useState } from 'react';

// How many hits to pull per request (Pixabay allows 3–200; 30 keeps the
// grid filling smoothly without over-fetching).
export const PER_PAGE = 30;

/**
 * Paginated Pixabay fetching with "load more" (infinite scroll) support.
 *
 * Re-fetches from page 1 whenever `params` change (search term, filters, …)
 * and appends subsequent pages via `loadMore()`. Empty param values are
 * omitted so optional filters stay off until set.
 *
 * @param {string} endpoint  e.g. 'https://pixabay.com/api/' or '.../api/videos/'
 * @param {object} params    query params (q, image_type, orientation, …)
 */
export default function useInfinitePixabay(endpoint, params) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);     // total available (headline count)
    const [loading, setLoading] = useState(true);       // first page
    const [loadingMore, setLoadingMore] = useState(false); // subsequent pages
    const [error, setError] = useState('');
    const [hasMore, setHasMore] = useState(false);

    // Latest params, read inside callbacks without making them a dependency.
    const paramsRef = useRef(params);
    paramsRef.current = params;
    // Only re-run the reset effect when params meaningfully change.
    const key = JSON.stringify(params);

    const pageRef = useRef(1);
    const loadingRef = useRef(false); // guards against overlapping requests

    const fetchPage = useCallback(async (page) => {
        const usp = new URLSearchParams({
            key: process.env.REACT_APP_PIXABAY_API_KEY,
            per_page: String(PER_PAGE),
            page: String(page),
            pretty: 'true',
        });
        Object.entries(paramsRef.current).forEach(([k, v]) => {
            if (v !== '' && v !== undefined && v !== null) usp.set(k, String(v));
        });
        const res = await fetch(`${endpoint}?${usp.toString()}`);
        if (!res.ok) throw new Error(`Pixabay request failed (${res.status})`);
        return res.json();
    }, [endpoint]);

    // Reset and load the first page whenever the query/filters change.
    useEffect(() => {
        let cancelled = false;
        pageRef.current = 1;
        loadingRef.current = true;
        setLoading(true);
        setError('');

        fetchPage(1)
            .then(data => {
                if (cancelled) return;
                setItems(data.hits);
                setTotal(data.total);
                // Pixabay caps accessible results at `totalHits`; use it (not
                // `total`) to bound pagination and avoid out-of-range errors.
                setHasMore(data.hits.length > 0 && data.hits.length < data.totalHits);
                setLoading(false);
                loadingRef.current = false;
            })
            .catch(err => {
                if (cancelled) return;
                console.log(err);
                setError('Could not load results. Check your Pixabay API key.');
                setHasMore(false);
                setLoading(false);
                loadingRef.current = false;
            });

        return () => { cancelled = true; };
    }, [key, fetchPage]);

    const loadMore = useCallback(() => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setLoadingMore(true);

        const next = pageRef.current + 1;
        fetchPage(next)
            .then(data => {
                setItems(prev => {
                    const combined = [...prev, ...data.hits];
                    setHasMore(data.hits.length > 0 && combined.length < data.totalHits);
                    return combined;
                });
                pageRef.current = next;
                setLoadingMore(false);
                loadingRef.current = false;
            })
            .catch(err => {
                console.log(err);
                setHasMore(false); // stop trying on error (e.g. page out of range)
                setLoadingMore(false);
                loadingRef.current = false;
            });
    }, [fetchPage]);

    return { items, total, loading, loadingMore, error, hasMore, loadMore };
}
